import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { CompositeSelect } from "components/InputStyled";

export const ALL_DIMENSION_VALUES = Object.freeze({ mode: "all", includedIds: [] });

export function selectedDimensionValueIds(selection, items) {
  if (!selection || selection.mode === "all") {
    return new Set((items || []).map((item) => item.id));
  }
  const includedIds = new Set(selection.includedIds || []);
  return new Set((items || []).filter((item) => includedIds.has(item.id)).map((item) => item.id));
}

export function includeDimensionValueIds(ids) {
  return { mode: "include", includedIds: [...new Set(ids)] };
}

export function updateDimensionValueIds(selection, items, targetIds, selected) {
  const next = selectedDimensionValueIds(selection, items);
  const targets = new Set(targetIds);
  (items || []).forEach((item) => {
    if (!targets.has(item.id) || item.disabled) return;
    if (selected) next.add(item.id);
    else next.delete(item.id);
  });
  return next.size === items.length
    ? ALL_DIMENSION_VALUES
    : includeDimensionValueIds(items.filter((item) => next.has(item.id)).map((item) => item.id));
}

export function DimensionValueChecklist({ label = "Values", items = [], selection = ALL_DIMENSION_VALUES, onChange }) {
  const [query, setQuery] = useState("");
  useEffect(() => { setQuery(""); }, [label]);
  const selectedIds = useMemo(() => selectedDimensionValueIds(selection, items), [items, selection]);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleItems = useMemo(() => {
    if (!normalizedQuery) return items;
    return items.filter((item) =>
      [item.label, ...(item.path || [])].filter(Boolean).join(" ").toLocaleLowerCase().includes(normalizedQuery)
    );
  }, [items, normalizedQuery]);

  const emitIds = (ids) => {
    if (ids.size === items.length) onChange?.(ALL_DIMENSION_VALUES);
    else onChange?.(includeDimensionValueIds(items.filter((item) => ids.has(item.id)).map((item) => item.id)));
  };
  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    emitIds(next);
  };
  const updateVisible = (selected) => {
    onChange?.(updateDimensionValueIds(selection, items, visibleItems.map((item) => item.id), selected));
  };

  return (
    <div>
      <div className="flex items-center gap-2 rounded-md border border-border bg-app px-2">
        <Search size={15} className="shrink-0 text-muted" aria-hidden="true" />
        <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${label.toLocaleLowerCase()}…`} aria-label={`Search ${label}`} className="h-9 min-w-0 flex-1 bg-transparent text-app outline-none placeholder:text-muted" />
        {query ? <button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="text-muted hover:text-app"><X size={15} /></button> : null}
      </div>

      <div className="mt-3 flex items-center gap-3 border-b border-border pb-2 text-xs">
        <button type="button" className="linkCls" onClick={() => updateVisible(true)} disabled={!visibleItems.length}>{normalizedQuery ? "Select results" : "Select all"}</button>
        <button type="button" className="linkCls" onClick={() => updateVisible(false)} disabled={!visibleItems.length}>{normalizedQuery ? "Clear results" : "Clear all"}</button>
        <button type="button" className="linkCls ml-auto" onClick={() => onChange?.(ALL_DIMENSION_VALUES)}>Reset</button>
      </div>

      <div className="mt-2 max-h-72 overflow-auto">
        {visibleItems.length ? visibleItems.map((item) => {
          const checked = selectedIds.has(item.id);
          const pathLabel = item.path?.length > 1 ? item.path.join(" › ") : "";
          return (
            <label key={item.id} className="flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-xs hover:bg-muted/20">
              <input type="checkbox" checked={checked} onChange={() => toggle(item.id)} disabled={item.disabled} className="mt-0.5" />
              <span className="min-w-0 flex-1">
                <span className="block break-words text-app">{item.label}</span>
                {pathLabel ? <span className="block truncate text-[11px] text-muted">{pathLabel}</span> : null}
              </span>
            </label>
          );
        }) : <div className="px-2 py-5 text-center text-muted">No matching values.</div>}
      </div>
    </div>
  );
}

export function DimensionValueCompositeSelect({
  label = "Values",
  summary,
  items = null,
  selection = ALL_DIMENSION_VALUES,
  onChange,
  children = null,
  className = "w-64",
  menuClassName = "w-80",
  ariaLabel,
  checklistLabel = label,
}) {
  const selectedCount = items
    ? selectedDimensionValueIds(selection, items).size
    : 0;
  const selectionSummary = items
    ? selection?.mode === "all"
      ? `All (${items.length})`
      : selectedCount
        ? `${selectedCount} of ${items.length}`
        : "None"
    : "";

  return (
    <CompositeSelect
      label={label}
      summary={summary ?? selectionSummary}
      className={className}
      menuClassName={menuClassName}
      ariaLabel={ariaLabel || `Configure ${label.toLocaleLowerCase()}`}
    >
      {children}
      {items ? (
        <DimensionValueChecklist
          label={checklistLabel}
          items={items}
          selection={selection}
          onChange={onChange}
        />
      ) : null}
    </CompositeSelect>
  );
}

export default function DimensionValueFilter({ label = "Values", items = [], selection = ALL_DIMENSION_VALUES, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const selectedCount = selectedDimensionValueIds(selection, items).size;
  const triggerText = selection?.mode === "all"
    ? `${label}: All (${items.length})`
    : `${label}: ${selectedCount} of ${items.length}`;

  return (
    <div className="relative" ref={rootRef}>
      <button type="button" className="btn btn-secondary inline-flex items-center gap-2 text-sm" onClick={() => setOpen((current) => !current)} aria-haspopup="dialog" aria-expanded={open}>
        <span>{triggerText}</span>
        <ChevronDown size={15} aria-hidden="true" />
      </button>
      {open ? (
        <div role="dialog" aria-label={`Filter by ${label}`} className="absolute right-0 z-30 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-app p-3 text-sm shadow-xl">
          <DimensionValueChecklist label={label} items={items} selection={selection} onChange={onChange} />
        </div>
      ) : null}
    </div>
  );
}
